'use client';

import { useFinancialStore } from "@/store/useFinancialStore";
import GlassCard from '../components/GlassCard';
import GlassButton from '../components/GlassButton';
import PremiumEmptyState from '../components/PremiumEmptyState';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PortfolioPerformanceChart, AssetAllocationChart } from '@/components/DashboardCharts';
import OptimizationGrid from '@/components/OptimizationGrid';

// Mock data for initial visualization ignition
const performanceData = [
  { time: 'Mon', value: 85000 },
  { time: 'Tue', value: 87200 },
  { time: 'Wed', value: 86500 },
  { time: 'Thu', value: 91000 },
  { time: 'Fri', value: 94000 },
  { time: 'Sat', value: 96800 },
  { time: 'Sun', value: 98000 },
];

export default function PortfolioPage() {
  const { portfolio, positions } = useFinancialStore();

  const totalReturn = portfolio ? (portfolio.totalValue - portfolio.cashBalance) : 0;
  const totalReturnPercent = portfolio ? (totalReturn / portfolio.totalValue) * 100 : 0;

  const portfolioStats = {
    totalValue: portfolio?.totalValue || 0,
    cashBalance: portfolio?.cashBalance || 0,
    totalReturn: totalReturn,
    totalReturnPercent: totalReturnPercent,
    dailyChange: portfolio?.dailyChange || 0,
    dailyChangePercent: portfolio?.dailyChangePercent || 0,
  };

  const allocationData = positions.map((p, i) => ({
    name: p.symbol,
    value: p.totalValue,
    color: ['#00FFDE', '#9E24FF', '#FACC15', '#64748B'][i % 4]
  }));

  if (portfolioStats.cashBalance > 0) {
    allocationData.push({ name: 'Liquidity', value: portfolioStats.cashBalance, color: '#334155' });
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Header */}
      <header className="bg-bg-secondary/40 backdrop-blur-2xl border-b border-white/5 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex justify-between items-center">
            <div className="flex items-center gap-6">
              <Link to="/dashboard" className="p-3 bg-white/5 rounded-2xl hover:bg-white/10 transition-all border border-white/5 group">
                <svg className="w-5 h-5 text-text-tertiary group-hover:text-brand-teal-light transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div className="space-y-0.5">
                <h1 className="text-sm font-black tracking-[0.3em] text-text-tertiary uppercase">Institutional Ledger</h1>
                <p className="text-xl font-bold tracking-tight text-text-primary">Treasury <span className="text-brand-teal-light">& Allocation</span></p>
              </div>
            </div>
            <div className="flex items-center gap-4">
               <GlassButton asChild variant="primary" size="sm" className="rounded-2xl px-6 h-12 uppercase tracking-widest font-black text-[10px]" glowEffect>
                <Link to="/trading">Execute Alpha</Link>
              </GlassButton>
            </div>
        </div>
      </header>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="max-w-7xl mx-auto px-6 lg:px-8 py-12 space-y-12"
      >
        {/* Portfolio Hub Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Asset Valuation', value: portfolioStats.totalValue, sub: portfolioStats.dailyChange, subPct: portfolioStats.dailyChangePercent, color: 'brand-teal' },
            { label: 'Available Liquidity', value: portfolioStats.cashBalance, sub: 'Cash Reserves', color: 'text-secondary' },
            { label: 'Gross Yield', value: portfolioStats.totalReturn, subPct: portfolioStats.totalReturnPercent, color: 'brand-plum' },
            { label: 'Active Exposure', value: positions.length, sub: 'Market Instruments', color: 'text-tertiary' }
          ].map((stat, i) => (
            <motion.div key={stat.label} variants={itemVariants}>
              <GlassCard variant="elevated" className="p-8 relative overflow-hidden group h-full">
                <div className={`absolute top-0 right-0 w-24 h-24 blur-3xl opacity-10 group-hover:opacity-20 transition-all bg-${stat.color.split('-')[0] === 'brand' ? stat.color : 'white'}`} />
                <h3 className="text-[10px] tracking-[0.2em] uppercase font-black text-text-tertiary mb-3">{stat.label}</h3>
                <div className="text-3xl tabular-nums tracking-tighter font-black text-text-primary">${typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</div>
                {typeof stat.sub === 'number' ? (
                  <div className={`mt-3 font-black text-[11px] tracking-widest flex items-center gap-2 ${stat.sub >= 0 ? 'text-brand-teal-light' : 'text-brand-plum-light'}`}>
                    <span>{stat.sub >= 0 ? '▲' : '▼'}</span>
                    <span>${Math.abs(stat.sub).toLocaleString()} ({Math.abs(stat.subPct || 0)}%)</span>
                  </div>
                ) : (
                  <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-text-muted opacity-50">{stat.sub}</div>
                )}
              </GlassCard>
            </motion.div>
          ))}
        </div>

        {/* Intelligence Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           <motion.div variants={itemVariants} className="lg:col-span-2">
              <GlassCard variant="elevated" className="p-8 h-full">
                 <div className="flex justify-between items-center mb-8">
                    <h3 className="text-xs font-black uppercase tracking-widest text-text-tertiary">Alpha Trajectory</h3>
                    <div className="px-3 py-1 bg-white/5 rounded-lg text-[9px] font-black uppercase tracking-widest text-text-muted">7D Interval</div>
                 </div>
                 <div className="h-[300px]">
                   <PortfolioPerformanceChart data={performanceData} />
                 </div>
              </GlassCard>
           </motion.div>
           
           <motion.div variants={itemVariants}>
              <GlassCard variant="elevated" className="p-8 h-full flex flex-col items-center">
                 <h3 className="self-start text-xs font-black uppercase tracking-widest text-text-tertiary mb-8">Diversification Matrix</h3>
                 <div className="relative w-full aspect-square max-w-[200px]">
                   <AssetAllocationChart data={allocationData} />
                 </div>
                 <div className="w-full mt-8 space-y-2">
                    {allocationData.slice(0, 4).map((item) => (
                      <div key={item.name} className="flex justify-between items-center p-3 bg-white/5 rounded-2xl border border-white/5">
                        <div className="flex items-center gap-3">
                           <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                           <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary truncate">{item.name}</span>
                        </div>
                        <span className="text-[10px] font-mono font-bold text-text-primary">
                           {((item.value / portfolioStats.totalValue) * 100).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                 </div>
              </GlassCard>
           </motion.div>
        </div>

        {/* Neural Optimization (OPP-04) */}
        <motion.div variants={itemVariants}>
           <OptimizationGrid />
        </motion.div>

        {/* Positions Table */}
        <motion.div variants={itemVariants}>
          <GlassCard variant="elevated" className="p-0 overflow-hidden border border-white/5">
            <div className="flex justify-between items-center p-8 bg-white/[0.02] border-b border-white/5">
              <div className="space-y-1">
                <h2 className="text-xl font-black text-text-primary tracking-tighter uppercase">Market <span className="text-brand-teal-light">Exposure</span></h2>
                <p className="text-[10px] text-text-tertiary font-bold uppercase tracking-widest opacity-50">Active Ledger Components</p>
              </div>
              <GlassButton asChild variant="ghost" className="text-[10px] font-black uppercase tracking-widest border-white/10 hover:bg-brand-teal/10">
                <Link to="/trading">Tactical Entry</Link>
              </GlassButton>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-bg-primary/30 text-[10px] tracking-[0.2em] font-black uppercase text-text-tertiary border-b border-white/5">
                    <th className="py-6 px-8">Asset Identifier</th>
                    <th className="text-right py-6 px-8">Quantity</th>
                    <th className="text-right py-6 px-8">Entry Basis</th>
                    <th className="text-right py-6 px-8">Neural Price</th>
                    <th className="text-right py-6 px-8">Notional</th>
                    <th className="text-right py-6 px-8">Alpha Delta</th>
                    <th className="text-center py-6 px-8">Operations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm font-medium">
                  <AnimatePresence>
                    {positions.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-0">
                           <div className="min-h-[300px] flex items-center justify-center">
                              <PremiumEmptyState title="No active positions" description="Your capital is currently sidelined. Initialize deployment to capture alpha." />
                           </div>
                        </td>
                      </tr>
                    ) : (positions.map((position, idx) => (
                      <motion.tr 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + idx * 0.05 }}
                        key={position.symbol} 
                        className="hover:bg-white/[0.04] transition-all group"
                      >
                        <td className="py-6 px-8">
                           <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-text-primary font-black text-xs border border-white/10 group-hover:border-brand-teal/30 transition-all">
                                 {position.symbol[0]}
                              </div>
                              <span className="text-text-primary font-black tracking-tight text-lg group-hover:text-brand-teal-light transition-colors">{position.symbol}</span>
                           </div>
                        </td>
                        <td className="py-6 px-8 text-right tabular-nums text-text-secondary font-mono tracking-tighter">{position.quantity}</td>
                        <td className="py-6 px-8 text-right tabular-nums text-text-tertiary font-mono tracking-tighter">${position.averagePrice.toFixed(2)}</td>
                        <td className="py-6 px-8 text-right tabular-nums text-text-primary font-mono tracking-tighter font-bold">${position.currentPrice.toFixed(2)}</td>
                        <td className="py-6 px-8 text-right tabular-nums text-text-primary font-mono tracking-tighter font-bold">${position.totalValue.toLocaleString()}</td>
                        <td className={`py-6 px-8 text-right tabular-nums font-black tracking-widest text-[11px] ${position.unrealizedPnl >= 0 ? 'text-brand-teal-light' : 'text-brand-plum-light'}`}>
                          {position.unrealizedPnl >= 0 ? '▲' : '▼'} {Math.abs(position.unrealizedPnlPercent).toFixed(2)}%
                          <div className="text-[9px] opacity-50 mt-0.5">${Math.abs(position.unrealizedPnl).toLocaleString()}</div>
                        </td>
                        <td className="py-6 px-8">
                          <div className="flex justify-center gap-3">
                            <Link 
                              to={`/trading?symbol=${position.symbol}&side=BUY`}
                              className="p-2 rounded-xl bg-brand-teal/10 text-brand-teal-light hover:bg-brand-teal/20 transition-all border border-brand-teal/20"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            </Link>
                            <Link 
                              to={`/trading?symbol=${position.symbol}&side=SELL`}
                              className="p-2 rounded-xl bg-brand-plum/10 text-brand-plum-light hover:bg-brand-plum/20 transition-all border border-brand-plum/20"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                            </Link>
                          </div>
                        </td>
                      </motion.tr>
                    )))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </GlassCard>
        </motion.div>
      </motion.div>
    </div>
  );
}